import { useEffect } from 'react';

/**
 * Custom hook to handle keyboard visibility and input focusing on mobile devices
 * @param {Object} options - Configuration options
 * @param {boolean} options.isOnPhone - Whether the user is on a phone device
 * @param {React.RefObject} options.inputRef - Reference to the input element
 * @param {React.RefObject} options.inputContainerRef - Reference to the input container
 * @param {React.RefObject} options.conversationContainerRef - Reference to the conversation container
 */
export const useKeyboardVisibility = ({
  isOnPhone,
  inputRef,
  inputContainerRef,
  conversationContainerRef
}) => {
  // Handle keyboard focus and scrolling for mobile devices
  useEffect(() => {
    if (!isOnPhone) return;

    const handleKeyboardVisibility = () => {
      const input = inputRef.current;

      const handleFocus = () => {
        // When focused, add a small delay and scroll input into view
        setTimeout(() => {
          if (inputContainerRef.current) {
            inputContainerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 300);
      };

      if (input) {
        input.addEventListener('focus', handleFocus);

        return () => {
          input.removeEventListener('focus', handleFocus);
        };
      }
    };

    const cleanup = handleKeyboardVisibility();

    return () => {
      if (cleanup) cleanup();
    };
  }, [isOnPhone, inputRef, inputContainerRef]);

  // Prevent scroll on body when keyboard is open on mobile
  useEffect(() => {
    if (isOnPhone) {
      // When on mobile, add styling to prevent body scrolling when keyboard is open
      const preventScroll = () => {
        document.body.style.height = '100%';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      };

      const restoreScroll = () => {
        document.body.style.height = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };

      const handleFocus = () => {
        preventScroll();
      };

      const handleBlur = () => {
        restoreScroll();
      };

      if (inputRef.current) {
        inputRef.current.addEventListener('focus', handleFocus);
        inputRef.current.addEventListener('blur', handleBlur);
      }

      return () => {
        restoreScroll();
        if (inputRef.current) {
          inputRef.current.removeEventListener('focus', handleFocus);
          inputRef.current.removeEventListener('blur', handleBlur);
        }
      };
    }
  }, [isOnPhone, inputRef]);

  // Add extra padding at the bottom when on mobile
  useEffect(() => {
    const adjustBottomPadding = () => {
      if (isOnPhone && conversationContainerRef.current) {
        conversationContainerRef.current.style.paddingBottom = '150px';
      } else if (conversationContainerRef.current) {
        conversationContainerRef.current.style.paddingBottom = '16px';
      }
    };

    adjustBottomPadding();

    // Add event listener for resize
    window.addEventListener('resize', adjustBottomPadding);

    return () => {
      window.removeEventListener('resize', adjustBottomPadding);
    };
  }, [isOnPhone, conversationContainerRef]);
};